import { isBlockEnabled } from "@/src/lib/blocks/utils";
import { getNestedValue, hasValue } from "@/src/lib/utils/object-utils";
import { ServiceLine } from "@/src/models/constants/service-lines";
import { Specialty } from "@/src/models/constants/specialty";
import { type File } from "@/src/models/file";
import { type BlockConfigFile } from "@/src/models/intake/blocks/block-config";
import { BlockKey, BlockType, getBlockTypeFromKey } from "@/src/models/intake/blocks/block-types";
import { getBlockKeysForClassification } from "@/src/models/intake/blocks/enriched-content";
import { PersonGender } from "@/src/models/person";
import { type Section } from "@/src/models/section";

type HasValueAtPath = (path: string) => boolean;

export enum AutoIntakeFileFailureReason {
  CROPPED_PAGES = "cropped_pages",
  MULTIPLE_PATIENTS = "multiple_patients",
  VISUAL_DUPLICATE = "visual_duplicate",
}

export enum AutoIntakeBlockFailureReason {
  IMAGING_PROCEDURE_MISSING = "imaging_procedure_missing",
  LOCATION_NOT_GEOCODED = "location_not_geocoded",
  PATIENT_GENDER_UNKNOWN = "patient_gender_unknown",
  PATIENT_HOME_ADDRESS_MISSING = "patient_home_address_missing",
  PHONE_NUMBER_MISSING = "phone_number_missing",
  PRIMARY_COVERAGE_PAYER_NOT_MATCHED = "primary_coverage_payer_not_matched",
  PROVIDER_BLOCK_NPI_NOT_MATCHED = "provider_block_npi_not_matched",
  PROVIDER_NPI_NOT_MATCHED = "provider_npi_not_matched",
  RECEIVING_LOCATION_NOT_GEOCODED = "receiving_location_not_geocoded",
  RECEIVING_LOCATION_NOT_MATCHED = "receiving_location_not_matched",
  RECEIVING_PROVIDER_NPI_NOT_MATCHED = "receiving_provider_npi_not_matched",
  REFERRING_LOCATION_NOT_GEOCODED = "referring_location_not_geocoded",
  REFERRING_LOCATION_NOT_MATCHED = "referring_location_not_matched",
  REFERRING_PROVIDER_NPI_NOT_MATCHED = "referring_provider_npi_not_matched",
  SERVICE_LINE_NOT_INDICATED = "service_line_not_indicated",
  SPECIALTY_UNCLEAR = "specialty_unclear",
}

export const AutoIntakeFileCheck = {
  NO_CROPPED_PAGES: {
    failureReason: AutoIntakeFileFailureReason.CROPPED_PAGES,
    passes: (file: Pick<File, "croppedPageIndices"> | null | undefined) =>
      (file?.croppedPageIndices?.length ?? 0) === 0,
  },
  // Set when reception dedupe matched an earlier file and staff did not reject the match.
  NO_VISUAL_DUPLICATE: {
    failureReason: AutoIntakeFileFailureReason.VISUAL_DUPLICATE,
    passes: (file: Pick<File, "duplicatesFileId"> | null | undefined) => !file?.duplicatesFileId,
  },
  SINGLE_PATIENT: {
    failureReason: AutoIntakeFileFailureReason.MULTIPLE_PATIENTS,
    passes: (file: Pick<File, "hasMultiplePatients"> | null | undefined) => !file?.hasMultiplePatients,
  },
} as const;

export type AutoIntakeFileCheck = (typeof AutoIntakeFileCheck)[keyof typeof AutoIntakeFileCheck];

export const AutoIntakeBlockCheck = {
  MATCHED_ALL_PROVIDER_NPIS: {
    blockType: BlockType.PROVIDER,
    failureReason: AutoIntakeBlockFailureReason.PROVIDER_NPI_NOT_MATCHED,
    passes: (has: HasValueAtPath, _initialReviewedFields: unknown) => has("npiNumber"),
  },
  MATCHED_PROVIDER_BLOCK_NPI: {
    blockType: BlockType.PROVIDER,
    blockKey: BlockKey.PROVIDER,
    failureReason: AutoIntakeBlockFailureReason.PROVIDER_BLOCK_NPI_NOT_MATCHED,
    passes: (has: HasValueAtPath, _initialReviewedFields: unknown) => has("npiNumber"),
  },
  MATCHED_REFERRING_PROVIDER_NPI: {
    blockType: BlockType.PROVIDER,
    blockKey: BlockKey.REFERRING_PROVIDER,
    failureReason: AutoIntakeBlockFailureReason.REFERRING_PROVIDER_NPI_NOT_MATCHED,
    passes: (has: HasValueAtPath, _initialReviewedFields: unknown) => has("npiNumber"),
  },
  MATCHED_RECEIVING_PROVIDER_NPI: {
    blockType: BlockType.PROVIDER,
    blockKey: BlockKey.RECEIVING_PROVIDER,
    failureReason: AutoIntakeBlockFailureReason.RECEIVING_PROVIDER_NPI_NOT_MATCHED,
    passes: (has: HasValueAtPath, _initialReviewedFields: unknown) => has("npiNumber"),
  },
  GEOCODED_LOCATION: {
    blockType: BlockType.LOCATION,
    failureReason: AutoIntakeBlockFailureReason.LOCATION_NOT_GEOCODED,
    passes: (has: HasValueAtPath, _initialReviewedFields: unknown) => has("placeId") && has("address.line1"),
  },
  GEOCODED_REFERRING_LOCATION: {
    blockType: BlockType.LOCATION,
    blockKey: BlockKey.REFERRING_LOCATION,
    failureReason: AutoIntakeBlockFailureReason.REFERRING_LOCATION_NOT_GEOCODED,
    passes: (has: HasValueAtPath, _initialReviewedFields: unknown) => has("placeId") && has("address.line1"),
  },
  MATCHED_REFERRING_LOCATION: {
    blockType: BlockType.LOCATION,
    blockKey: BlockKey.REFERRING_LOCATION,
    failureReason: AutoIntakeBlockFailureReason.REFERRING_LOCATION_NOT_MATCHED,
    passes: (has: HasValueAtPath, _initialReviewedFields: unknown) => has("groupId"),
  },
  GEOCODED_RECEIVING_LOCATION: {
    blockType: BlockType.LOCATION,
    blockKey: BlockKey.RECEIVING_LOCATION,
    failureReason: AutoIntakeBlockFailureReason.RECEIVING_LOCATION_NOT_GEOCODED,
    passes: (has: HasValueAtPath, _initialReviewedFields: unknown) => has("placeId") && has("address.line1"),
  },
  // Receiving locations are roster-only, so an unmatched block is blank and a matched one carries the roster id.
  MATCHED_RECEIVING_LOCATION: {
    blockType: BlockType.LOCATION,
    blockKey: BlockKey.RECEIVING_LOCATION,
    failureReason: AutoIntakeBlockFailureReason.RECEIVING_LOCATION_NOT_MATCHED,
    passes: (has: HasValueAtPath, _initialReviewedFields: unknown) => has("id"),
  },
  PATIENT_PHONE_NUMBER: {
    blockType: BlockType.PATIENT,
    failureReason: AutoIntakeBlockFailureReason.PHONE_NUMBER_MISSING,
    passes: (has: HasValueAtPath, _initialReviewedFields: unknown) =>
      has("mobilePhoneNumber") || has("homePhoneNumber"),
  },
  PATIENT_HOME_ADDRESS: {
    blockType: BlockType.PATIENT,
    failureReason: AutoIntakeBlockFailureReason.PATIENT_HOME_ADDRESS_MISSING,
    passes: (has: HasValueAtPath, _initialReviewedFields: unknown) => has("homeAddress.line1"),
  },
  PATIENT_GENDER_KNOWN: {
    blockType: BlockType.PATIENT,
    failureReason: AutoIntakeBlockFailureReason.PATIENT_GENDER_UNKNOWN,
    passes: (has: HasValueAtPath, initialReviewedFields: unknown) =>
      has("gender") && getNestedValue(initialReviewedFields, "gender") !== PersonGender.UNKNOWN,
  },
  // Scoped to primary coverage: secondary coverage is legitimately empty on most documents.
  MATCHED_PRIMARY_COVERAGE_PAYER: {
    blockType: BlockType.COVERAGE,
    blockKey: BlockKey.PRIMARY_COVERAGE,
    failureReason: AutoIntakeBlockFailureReason.PRIMARY_COVERAGE_PAYER_NOT_MATCHED,
    passes: (has: HasValueAtPath, _initialReviewedFields: unknown) => has("customerPayer"),
  },
  DETERMINED_IMAGING_PROCEDURE: {
    blockType: BlockType.IMAGING_RESULTS,
    failureReason: AutoIntakeBlockFailureReason.IMAGING_PROCEDURE_MISSING,
    passes: (has: HasValueAtPath, _initialReviewedFields: unknown) => has("procedure"),
  },
  DETERMINED_SPECIALTY: {
    blockType: BlockType.DOCUMENT_INFORMATION,
    blockKey: BlockKey.DOCUMENT_INFORMATION,
    failureReason: AutoIntakeBlockFailureReason.SPECIALTY_UNCLEAR,
    passes: (_has: HasValueAtPath, initialReviewedFields: unknown) => {
      const specialties = getNestedValue(initialReviewedFields, "specialties");

      return (
        Array.isArray(specialties) &&
        specialties.length > 0 &&
        specialties.every(
          (specialty) => typeof specialty === "string" && specialty.trim() !== "" && specialty !== Specialty.UNCLEAR
        )
      );
    },
  },
  DETERMINED_SERVICE_LINE: {
    blockType: BlockType.REFERRAL_INFORMATION,
    blockKey: BlockKey.REFERRAL_INFORMATION,
    failureReason: AutoIntakeBlockFailureReason.SERVICE_LINE_NOT_INDICATED,
    passes: (_has: HasValueAtPath, initialReviewedFields: unknown) => {
      const serviceLine = getNestedValue(initialReviewedFields, "serviceLine");

      return typeof serviceLine === "string" && serviceLine.trim() !== "" && serviceLine !== ServiceLine.NOT_INDICATED;
    },
  },
} as const;

export type AutoIntakeBlockCheck = (typeof AutoIntakeBlockCheck)[keyof typeof AutoIntakeBlockCheck];
export type FileEligibilityFailureReason = AutoIntakeFileCheck["failureReason"];
export type BlockEligibilityFailureReason = AutoIntakeBlockCheck["failureReason"];

export function getFileEligibilityFailureReason(
  file: Pick<File, "croppedPageIndices" | "duplicatesFileId" | "hasMultiplePatients"> | null | undefined,
  checks: readonly AutoIntakeFileCheck[]
): FileEligibilityFailureReason | null {
  for (const check of checks) {
    if (!check.passes(file)) {
      return check.failureReason;
    }
  }

  return null;
}

export function getBlockEligibilityFailureReason(
  section: Section,
  blockConfig: BlockConfigFile | undefined | null,
  checks: readonly AutoIntakeBlockCheck[]
): BlockEligibilityFailureReason | null {
  const enrichedContent = section.enrichedContent;

  for (const blockKey of getBlockKeysForClassification(section.classification)) {
    if (!isBlockEnabled(blockConfig, blockKey)) {
      continue;
    }

    const initialReviewedFields = enrichedContent?.[blockKey]?.initialReviewedFields;
    const has = (path: string): boolean => hasValue(getNestedValue(initialReviewedFields, path));
    const blockType = getBlockTypeFromKey(blockKey);

    for (const check of checks) {
      const appliesToBlock =
        (!("blockType" in check) || check.blockType === blockType) &&
        (!("blockKey" in check) || check.blockKey === blockKey);

      if (appliesToBlock && !check.passes(has, initialReviewedFields)) {
        return check.failureReason;
      }
    }
  }

  return null;
}
